'use client';

export type SeoIssue = {
  severity: 'error' | 'warning' | 'info';
  title: string;
  description: string;
};

export type RobotsDirective = {
  key: string;
  value: string;
  line: number;
};

export type RobotsBlock = {
  userAgents: string[];
  directives: RobotsDirective[];
};

export type RobotsValidationResult = {
  blocks: RobotsBlock[];
  sitemapUrls: string[];
  issues: SeoIssue[];
};

export type SitemapEntry = {
  loc: string;
  lastmod: string | null;
};

export type SitemapInspectionResult = {
  type: 'urlset' | 'sitemapindex';
  urls: SitemapEntry[];
  childSitemaps: SitemapEntry[];
  issues: SeoIssue[];
};

export type CanonicalCheckResult = {
  canonicals: string[];
  issues: SeoIssue[];
};

export type HreflangEntry = {
  hreflang: string;
  href: string;
};

export type HreflangCheckResult = {
  entries: HreflangEntry[];
  issues: SeoIssue[];
};

export type FaqSchemaItem = {
  question: string;
  answer: string;
};

export type ArticleSchemaInput = {
  articleType: 'Article' | 'BlogPosting';
  headline: string;
  description: string;
  url: string;
  image: string;
  author: string;
  publisher: string;
  datePublished: string;
  dateModified: string;
};

export type ProductSchemaInput = {
  name: string;
  description: string;
  url: string;
  image: string;
  brand: string;
  sku: string;
  price: string;
  currency: string;
  availability: string;
  ratingValue: string;
  ratingCount: string;
};

export type OpenGraphImageAssessment = {
  width: number;
  height: number;
  aspectRatio: string;
  aspectRatioDecimal: number;
  issues: SeoIssue[];
};

function normalizeLineEndings(value: string) {
  return value.replace(/\r\n?/g, '\n');
}

function escapeHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function gcd(left: number, right: number): number {
  let a = Math.abs(Math.round(left));
  let b = Math.abs(Math.round(right));

  while (b) {
    [a, b] = [b, a % b];
  }

  return a || 1;
}

function formatAspectRatio(width: number, height: number) {
  if (width <= 0 || height <= 0) {
    return 'Unknown';
  }

  const divisor = gcd(width, height);
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
}

function parseHtmlDocument(input: string) {
  const parser = new DOMParser();
  const normalized =
    input.trim().startsWith('<html') || input.includes('<body') || input.includes('<head')
      ? input
      : `<!doctype html><html><head></head><body>${input}</body></html>`;
  return parser.parseFromString(normalized, 'text/html');
}

function parseXmlDocument(input: string) {
  const parser = new DOMParser();
  const document = parser.parseFromString(input, 'application/xml');
  const parserError = document.querySelector('parsererror');
  if (parserError) {
    throw new Error('The XML could not be parsed.');
  }
  return document;
}

function looksLikeAbsoluteHttpUrl(value: string) {
  return /^https?:\/\//i.test(value.trim());
}

function slugifyFragment(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x00-\x7F]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function validateRobotsTxt(input: string): RobotsValidationResult {
  const lines = normalizeLineEndings(input)
    .split('\n')
    .map((line, index) => ({ raw: line, lineNumber: index + 1 }));

  const blocks: RobotsBlock[] = [];
  const sitemapUrls: string[] = [];
  const issues: SeoIssue[] = [];
  let currentBlock: RobotsBlock | null = null;

  for (const entry of lines) {
    // A '#' starts a comment anywhere on the line, including column 0 (RFC 9309).
    const hashIndex = entry.raw.indexOf('#');
    const withoutComment = (hashIndex === -1 ? entry.raw : entry.raw.slice(0, hashIndex)).trim();
    if (!withoutComment) {
      continue;
    }

    const separatorIndex = withoutComment.indexOf(':');
    if (separatorIndex === -1) {
      issues.push({
        severity: 'warning',
        title: 'Malformed line',
        description: `Line ${entry.lineNumber} does not contain a directive separator.`,
      });
      continue;
    }

    const key = withoutComment.slice(0, separatorIndex).trim().toLowerCase();
    const value = withoutComment.slice(separatorIndex + 1).trim();

    // "Disallow:" with an empty value is the canonical way to allow everything, not an error.
    if (!value && key !== 'disallow') {
      issues.push({
        severity: 'warning',
        title: 'Empty directive value',
        description: `Line ${entry.lineNumber} has an empty value for "${key}".`,
      });
    }

    if (key === 'sitemap') {
      sitemapUrls.push(value);
      if (value && !looksLikeAbsoluteHttpUrl(value)) {
        issues.push({
          severity: 'warning',
          title: 'Suspicious sitemap URL',
          description: `Line ${entry.lineNumber} contains a sitemap value that does not look like an absolute http(s) URL.`,
        });
      }
      continue;
    }

    if (key === 'user-agent') {
      const startsNewBlock = currentBlock && currentBlock.directives.length > 0;
      if (!currentBlock || startsNewBlock) {
        currentBlock = { userAgents: [], directives: [] };
        blocks.push(currentBlock);
      }
      currentBlock.userAgents.push(value || '*');
      continue;
    }

    if (!currentBlock) {
      issues.push({
        severity: 'warning',
        title: 'Directive before user-agent',
        description: `Line ${entry.lineNumber} appears before any user-agent block.`,
      });
      currentBlock = { userAgents: ['*'], directives: [] };
      blocks.push(currentBlock);
    }

    currentBlock.directives.push({ key, value, line: entry.lineNumber });
  }

  for (const block of blocks) {
    const seenDirectives = new Map<string, number>();
    for (const directive of block.directives) {
      const fingerprint = `${directive.key}:${directive.value}`;
      const seen = seenDirectives.get(fingerprint);
      if (typeof seen === 'number') {
        issues.push({
          severity: 'info',
          title: 'Duplicate directive',
          description: `User-agent block "${block.userAgents.join(', ')}" repeats "${directive.key}: ${directive.value}".`,
        });
      }
      seenDirectives.set(fingerprint, directive.line);
    }

    const crawlDelayEntries = block.directives.filter(
      (directive) => directive.key === 'crawl-delay',
    );
    if (crawlDelayEntries.length > 1) {
      issues.push({
        severity: 'warning',
        title: 'Multiple crawl-delay values',
        description: `User-agent block "${block.userAgents.join(', ')}" contains more than one crawl-delay directive.`,
      });
    }
  }

  if (!blocks.length) {
    if (!sitemapUrls.length) {
      throw new Error('Paste a robots.txt file first.');
    }
    // A file with only Sitemap: lines parses; it just tells no crawler anything.
    issues.push({
      severity: 'warning',
      title: 'No user-agent block',
      description:
        'This file declares a sitemap but no user-agent block, so it sets no crawl rules at all.',
    });
  }

  if (!issues.length) {
    issues.push({
      severity: 'info',
      title: 'No obvious robots issues found',
      description:
        'The pasted robots.txt parsed cleanly and did not show common duplicate or syntax problems.',
    });
  }

  return { blocks, sitemapUrls, issues };
}

export function inspectSitemapXml(input: string): SitemapInspectionResult {
  const document = parseXmlDocument(input);
  const rootName = document.documentElement.tagName.toLowerCase();
  const issues: SeoIssue[] = [];

  if (rootName !== 'urlset' && rootName !== 'sitemapindex') {
    throw new Error(
      'Paste a sitemap XML document with urlset or sitemapindex as the root element.',
    );
  }

  const urls: SitemapEntry[] = [];
  const childSitemaps: SitemapEntry[] = [];
  const seenLocs = new Set<string>();
  const duplicates = new Set<string>();

  if (rootName === 'urlset') {
    document.querySelectorAll('url').forEach((element) => {
      const loc = element.querySelector('loc')?.textContent?.trim() ?? '';
      const lastmod = element.querySelector('lastmod')?.textContent?.trim() ?? null;
      urls.push({ loc, lastmod });
    });
  } else {
    document.querySelectorAll('sitemap').forEach((element) => {
      const loc = element.querySelector('loc')?.textContent?.trim() ?? '';
      const lastmod = element.querySelector('lastmod')?.textContent?.trim() ?? null;
      childSitemaps.push({ loc, lastmod });
    });
  }

  [...urls, ...childSitemaps].forEach((entry) => {
    if (!entry.loc) {
      issues.push({
        severity: 'warning',
        title: 'Missing loc',
        description: 'One sitemap entry is missing a loc value.',
      });
      return;
    }

    if (!looksLikeAbsoluteHttpUrl(entry.loc)) {
      issues.push({
        severity: 'warning',
        title: 'Suspicious loc',
        description: `"${entry.loc}" does not look like an absolute http(s) URL.`,
      });
    }

    if (seenLocs.has(entry.loc)) {
      duplicates.add(entry.loc);
    }
    seenLocs.add(entry.loc);
  });

  duplicates.forEach((loc) => {
    issues.push({
      severity: 'info',
      title: 'Duplicate loc',
      description: `The sitemap repeats "${loc}".`,
    });
  });

  if (!urls.length && !childSitemaps.length) {
    issues.push({
      severity: 'warning',
      title: 'No entries found',
      description: 'The XML parsed, but no sitemap entries were detected.',
    });
  }

  if (!issues.length) {
    issues.push({
      severity: 'info',
      title: 'No obvious sitemap issues found',
      description:
        'The sitemap parsed cleanly and did not show common loc or duplication problems.',
    });
  }

  return {
    type: rootName as 'urlset' | 'sitemapindex',
    urls,
    childSitemaps,
    issues,
  };
}

export function checkCanonicalTags(input: string): CanonicalCheckResult {
  const document = parseHtmlDocument(input);
  const canonicals = Array.from(document.querySelectorAll('link[rel]'))
    .filter((element) =>
      (element.getAttribute('rel') ?? '').toLowerCase().split(/\s+/).includes('canonical'),
    )
    .map((element) => element.getAttribute('href')?.trim() ?? '')
    .filter(Boolean);

  const issues: SeoIssue[] = [];

  if (!canonicals.length) {
    issues.push({
      severity: 'warning',
      title: 'Missing canonical',
      description: 'No canonical tag was found in the pasted markup.',
    });
  }

  if (canonicals.length > 1) {
    issues.push({
      severity: 'warning',
      title: 'Multiple canonicals',
      description: 'More than one canonical tag was found in the pasted markup.',
    });
  }

  canonicals.forEach((canonical) => {
    if (!looksLikeAbsoluteHttpUrl(canonical)) {
      issues.push({
        severity: 'warning',
        title: 'Canonical is not absolute',
        description: `"${canonical}" does not look like an absolute http(s) URL.`,
      });
    }

    if (/[?#]/.test(canonical)) {
      issues.push({
        severity: 'info',
        title: 'Canonical includes query or fragment',
        description: `"${canonical}" includes a query string or fragment, which is worth reviewing manually.`,
      });
    }
  });

  if (!issues.length) {
    issues.push({
      severity: 'info',
      title: 'Canonical looks practical',
      description:
        'The pasted markup contains one canonical tag and it looks like an absolute URL.',
    });
  }

  return { canonicals, issues };
}

export function checkHreflangTags(input: string): HreflangCheckResult {
  const document = parseHtmlDocument(input);
  const entries = Array.from(document.querySelectorAll('link[rel][hreflang][href]'))
    .filter((element) =>
      (element.getAttribute('rel') ?? '').toLowerCase().split(/\s+/).includes('alternate'),
    )
    .map((element) => ({
      hreflang: element.getAttribute('hreflang')?.trim() ?? '',
      href: element.getAttribute('href')?.trim() ?? '',
    }))
    .filter((entry) => entry.hreflang || entry.href);

  const issues: SeoIssue[] = [];
  const seenCodes = new Map<string, string>();
  const hreflangPattern = /^(x-default|[a-z]{2,3}(-[A-Za-z0-9]{2,8})?)$/;

  if (!entries.length) {
    issues.push({
      severity: 'warning',
      title: 'No hreflang tags found',
      description: 'The pasted markup does not contain any alternate hreflang links.',
    });
  }

  for (const entry of entries) {
    const normalized = entry.hreflang.toLowerCase();
    if (!hreflangPattern.test(normalized)) {
      issues.push({
        severity: 'warning',
        title: 'Suspicious hreflang value',
        description: `"${entry.hreflang}" does not look like a typical language or x-default code.`,
      });
    }

    if (!looksLikeAbsoluteHttpUrl(entry.href)) {
      issues.push({
        severity: 'warning',
        title: 'Hreflang URL is not absolute',
        description: `"${entry.href}" does not look like an absolute http(s) URL.`,
      });
    }

    const seenHref = seenCodes.get(normalized);
    if (seenHref) {
      issues.push({
        severity: 'warning',
        title: 'Duplicate hreflang code',
        description: `The hreflang code "${entry.hreflang}" appears more than once.`,
      });
    }
    seenCodes.set(normalized, entry.href);
  }

  if (
    entries.length > 1 &&
    !entries.some((entry) => entry.hreflang.toLowerCase() === 'x-default')
  ) {
    issues.push({
      severity: 'info',
      title: 'Missing x-default',
      description: 'Multiple hreflang entries were found without an x-default fallback.',
    });
  }

  if (!issues.length) {
    issues.push({
      severity: 'info',
      title: 'No obvious hreflang issues found',
      description:
        'The pasted hreflang links look structurally reasonable in this practical checker.',
    });
  }

  return { entries, issues };
}

export function buildFaqSchema(items: FaqSchemaItem[]) {
  const normalized = items
    .map((item) => ({
      question: item.question.trim(),
      answer: item.answer.trim(),
    }))
    .filter((item) => item.question && item.answer);

  if (!normalized.length) {
    throw new Error('Add at least one FAQ question and answer first.');
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: normalized.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function buildArticleSchema(input: ArticleSchemaInput) {
  if (!input.headline.trim() || !input.url.trim()) {
    throw new Error('Add at least a headline and URL first.');
  }

  return {
    '@context': 'https://schema.org',
    '@type': input.articleType,
    headline: input.headline.trim(),
    description: input.description.trim() || undefined,
    url: input.url.trim(),
    image: input.image.trim() || undefined,
    author: input.author.trim()
      ? {
          '@type': 'Person',
          name: input.author.trim(),
        }
      : undefined,
    publisher: input.publisher.trim()
      ? {
          '@type': 'Organization',
          name: input.publisher.trim(),
        }
      : undefined,
    datePublished: input.datePublished || undefined,
    dateModified: input.dateModified || undefined,
  };
}

export function buildProductSchema(input: ProductSchemaInput) {
  if (!input.name.trim() || !input.url.trim()) {
    throw new Error('Add at least a product name and URL first.');
  }

  const priceText = input.price.trim();
  const ratingValueText = input.ratingValue.trim();
  const ratingCountText = input.ratingCount.trim();

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: input.name.trim(),
    description: input.description.trim() || undefined,
    url: input.url.trim(),
    image: input.image.trim() || undefined,
    brand: input.brand.trim()
      ? {
          '@type': 'Brand',
          name: input.brand.trim(),
        }
      : undefined,
    sku: input.sku.trim() || undefined,
    offers:
      priceText && input.currency.trim()
        ? {
            '@type': 'Offer',
            price: priceText,
            priceCurrency: input.currency.trim().toUpperCase(),
            availability: input.availability.trim() || 'https://schema.org/InStock',
            url: input.url.trim(),
          }
        : undefined,
    aggregateRating:
      ratingValueText && ratingCountText
        ? {
            '@type': 'AggregateRating',
            ratingValue: ratingValueText,
            reviewCount: ratingCountText,
          }
        : undefined,
  };
}

export function assessOpenGraphImage(width: number, height: number): OpenGraphImageAssessment {
  const issues: SeoIssue[] = [];
  const aspectRatioDecimal = width / height;
  const target = 1200 / 630;
  const ratioDelta = Math.abs(aspectRatioDecimal - target);

  if (width < 1200 || height < 630) {
    issues.push({
      severity: 'warning',
      title: 'Smaller than common OG guidance',
      description: 'The image is smaller than the common 1200×630 Open Graph target size.',
    });
  }

  if (ratioDelta > 0.08) {
    issues.push({
      severity: 'warning',
      title: 'Aspect ratio differs from common OG shape',
      description:
        'The image aspect ratio is noticeably different from the common Open Graph card shape.',
    });
  }

  if (!issues.length) {
    issues.push({
      severity: 'info',
      title: 'Looks close to a typical OG image',
      description: 'The dimensions and aspect ratio are close to common Open Graph image guidance.',
    });
  }

  return {
    width,
    height,
    aspectRatio: formatAspectRatio(width, height),
    aspectRatioDecimal,
    issues,
  };
}

export function cleanAltText(input: string) {
  const lines = normalizeLineEndings(input).split('\n');
  const cleanedLines = lines.map((line) => {
    const original = line.trim();
    if (!original) {
      return '';
    }

    const normalizedWhitespace = original
      .replace(/[ \t]+/g, ' ')
      .replace(/\s*([,.;:!?])\s*/g, '$1 ')
      .replace(/([,.;:!?]){2,}/g, '$1')
      .replace(/\s{2,}/g, ' ')
      .replace(/[-_]{2,}/g, ' ')
      .trim();

    const segments = normalizedWhitespace
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean);
    const dedupedSegments: string[] = [];
    const seen = new Set<string>();
    for (const segment of segments) {
      const fingerprint = slugifyFragment(segment);
      if (fingerprint && seen.has(fingerprint)) {
        continue;
      }
      if (fingerprint) {
        seen.add(fingerprint);
      }
      dedupedSegments.push(segment);
    }

    let cleaned = dedupedSegments.length ? dedupedSegments.join(', ') : normalizedWhitespace;

    const letters = cleaned.match(/[A-Za-zА-Яа-я]/g) ?? [];
    const uppercaseLetters = cleaned.match(/[A-ZА-Я]/g) ?? [];
    if (letters.length >= 6 && uppercaseLetters.length / letters.length > 0.8) {
      cleaned = cleaned.toLowerCase();
      cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }

    return cleaned
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([,.;:!?])/g, '$1')
      .trim();
  });

  return cleanedLines.join('\n').trim();
}

export async function loadOpenGraphImageFromUrl(url: string) {
  const trimmed = url.trim();
  if (!looksLikeAbsoluteHttpUrl(trimmed)) {
    throw new Error('Enter an absolute image URL first.');
  }

  return await new Promise<{ src: string; width: number; height: number }>((resolve, reject) => {
    const image = new Image();
    image.onload = () =>
      resolve({
        src: trimmed,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    image.onerror = () => reject(new Error('The image URL could not be loaded in this browser.'));
    image.src = trimmed;
  });
}

export function createSeoJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}

export function renderSeoIssuesHtml(issues: SeoIssue[]) {
  return issues
    .map(
      (issue) =>
        `<article class="tool-html-issue ${
          issue.severity === 'error'
            ? 'tool-html-issue-error'
            : issue.severity === 'warning'
              ? 'tool-html-issue-warning'
              : ''
        }"><strong>${escapeHtml(issue.title)}</strong><p>${escapeHtml(issue.description)}</p></article>`,
    )
    .join('');
}
